import React, { FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';

import CTA from 'Src/components/buttons/CTA';
import Copy from 'Src/components/Copy';
import { Flex } from 'Src/components/layout/Flex';
import { Check } from 'Src/feature/check/models';
import { TipSuggestion } from 'Src/feature/check/useCheck';
import { FlipAndTipLocationLogo } from 'Src/feature/flip-and-tip/FlipAndTipLocationLogo';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { useSignature } from 'Src/feature/signature/useSignature';

import { FlipAndTipCustomTipModal } from './FlipAndTipCustomTipModal';
import { FlipAndTipSignature } from './FlipAndTipSignature';
import { useFlipAndTipTotals } from './useFlipAndTipTotals';

import styles from './FlipAndTipForm.module.scss';

const TEST_ID = {
  root: `FlipAndTipForm-root`,
  final_total: `FlipAndTipForm-finalTotal`,
  tip_n_total: `FlipAndTipForm-tipAndTotal`,
};

interface FlipAndTipFormProps {
  className?: string;
  payment: Check['payments'][number] | undefined;
  tipSuggestions: TipSuggestion[]
  onComplete: (options: { tip: number, total: number, digitalSignature: string }) => void
}

const FlipAndTipForm: FC<FlipAndTipFormProps> = ({ className, payment, tipSuggestions, onComplete }) => {
  const { t } = useTranslation();
  const canvasRef = useRef(undefined as undefined | HTMLCanvasElement);
  const signature = useSignature({ canvasRef });

  const modal = {
    customTip: useModalControl(),
  };

  const totals = useFlipAndTipTotals({
    payment,
    defaultTipSuggestion: tipSuggestions[0]
  });

  const onContinueClick = () => {
    const digitalSignature = signature.toBase64();
    if (digitalSignature) {
      onComplete({ tip: totals.tipValue, total: totals.finalTotal, digitalSignature });
    }
  };

  return (
    <>
      <div
        data-testid={TEST_ID.root}
        className={classNames(className, styles.root)}
      >
        <div className={styles.content}>
          <FlipAndTipLocationLogo />
          <Copy
            data-testid={TEST_ID.final_total}
            variant="h4"
            align="center"
            value={t('labels.util.currency', { value: totals.finalTotal })}
          />
          <Copy
            data-testid={TEST_ID.tip_n_total}
            variant="body"
            weight="md"
            align="center"
            value={t('flipAndTip.labels.totalWithTip', { total: totals.amountAppliedToCheck, tip: totals.tipValue })}
          />
          <div className={styles.tip_group}>
            <CTA
              variant="outlined"
              fullHeight
              padding="md"
              selected={!totals.customTip && !totals.selectedTipSuggestion}
              onClick={totals.handleNoTipClick}
            >
              <Copy variant="button-label" align="center" value={t('flipAndTip.labels.noTip')} />
            </CTA>
            {tipSuggestions?.map((tipSuggestion) => (
              <CTA
                key={tipSuggestion.percent}
                variant="outlined"
                fullHeight
                padding="md"
                label={(
                  <Flex gap="none">
                    <Copy variant="button-label" align="center" value={t('labels.util.percent', { value: tipSuggestion.percent })} />
                    <Copy
                      variant="body"
                      size="sm"
                      align="center"
                      value={t('labels.util.currency', { value: tipSuggestion.tip })}
                    />
                  </Flex>
                )}
                selected={totals.selectedTipSuggestion?.percent === tipSuggestion.percent}
                onClick={() => totals.handleTipSuggestionClick(tipSuggestion)}
              />
            ))}
            <CTA
              className={styles.tip_custom}
              variant="outlined"
              fullHeight
              padding="md"
              fullWidth
              selected={totals.customTip !== undefined}
              onClick={() => modal.customTip.open()}
            >
              <Copy variant="button-label" align="center" value={t('flipAndTip.labels.customAmount')} />
            </CTA>
          </div>
          <FlipAndTipSignature signature={signature} canvasRef={canvasRef} />
          <CTA
            className={classNames(styles.submit_cta, { [styles.visible]: signature.ui === 'touched' })}
            pill
            color="primary"
            size="lg"
            fullWidth
            label={t('flipAndTip.labels.continue')}
            onClick={onContinueClick}
          />
        </div>
      </div>
      <FlipAndTipCustomTipModal
        isOpen={modal.customTip.isOpen}
        onClose={modal.customTip.close}
        onDone={(value) => {
          totals.handleCustomTipChange(value);
        }}
      />
    </>
  );
};

export { FlipAndTipForm, TEST_ID };
